import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { get, put } from "../lib/api";
import { useLoad } from "../lib/hooks";
import { formatEUR } from "../lib/reports";
import { Button, Card, ErrorBox, Field, Input, Loading, Spinner, Toggle } from "../ui";
import { useShell } from "../shell/context";

// Settings → Reports: the E8 keys of /v1/settings/crm.
export default function SettingsReports() {
    const { toast, bump } = useShell();
    const { data, error, loading, reload, setData } = useLoad(() => get("v1/settings/crm"), []);
    const [target, setTarget] = useState("");
    const [email, setEmail] = useState(true);
    const [busy, setBusy] = useState(false);
    const [formError, setFormError] = useState(null);

    const reset = (d) => {
        setTarget(d.quarterly_target_eur ? String(d.quarterly_target_eur) : "");
        setEmail(!!d.weekly_brief_email_enabled);
    };
    useEffect(() => {
        if (data) reset(data);
    }, [data]);

    if (loading && !data) return <Card><Loading /></Card>;
    if (error && !data) return <ErrorBox error={error} onRetry={reload} />;

    const targetNum = target.trim() === "" ? 0 : Number(target.replace(/[,\s]/g, ""));
    const changes = {};
    if (targetNum !== Number(data.quarterly_target_eur || 0)) changes.quarterly_target_eur = targetNum;
    if (email !== !!data.weekly_brief_email_enabled) changes.weekly_brief_email_enabled = email;
    const dirty = Object.keys(changes).length > 0;

    const save = async () => {
        if (!Number.isFinite(targetNum) || targetNum < 0 || targetNum >= 1e12) return setFormError("Quarterly target: a positive amount in EUR, or empty for no target.");
        setBusy(true);
        setFormError(null);
        try {
            const res = await put("v1/settings/crm", changes);
            setData(res);
            toast("Report settings saved.");
            bump();
        } catch (e) {
            setFormError(e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card
            title="Reports"
            footer={
                <div className="hz-row">
                    <span className="hz-spacer"></span>
                    <Button variant="light" disabled={!dirty || busy} onClick={() => reset(data)}>Reset</Button>
                    <Button variant="primary" disabled={!dirty || busy} onClick={save}>{busy && <Spinner />}Save</Button>
                </div>
            }
        >
            {formError && <div className="hz-alert hz-alert-danger" style={{ marginBottom: 12 }}>{formError}</div>}
            <div className="hz-form-grid" style={{ maxWidth: 560 }}>
                <Field
                    label="Quarterly turnover target (EUR)"
                    hint={
                        <>
                            One target for every quarter: won lead value (EUR at close) against it shows on the{" "}
                            <Link to="/reports/funnel">Funnel</Link> and the <Link to="/reports/weekly-brief">Weekly brief</Link>. Empty means no target.
                        </>
                    }
                >
                    <div className="hz-row" style={{ gap: 8 }}>
                        <Input type="number" min={0} step={1000} value={target} onChange={(e) => setTarget(e.target.value)} placeholder="e.g. 150000" style={{ maxWidth: 220 }} />
                        {targetNum > 0 && <span className="hz-muted">{formatEUR(targetNum)} per quarter</span>}
                    </div>
                </Field>
                <div>
                    <span className="hz-label">Weekly brief email</span>
                    <Toggle on={email} onChange={setEmail}>{email ? "On: sent Mondays at 07:00 (Istanbul)" : "Off: the brief is only on the Weekly brief page"}</Toggle>
                    <div className="hz-hint">When it is sent, that Monday's daily digest is skipped (the brief covers it). Needs the email sender to be configured on the server.</div>
                </div>
                <div className="hz-small hz-muted">Reporting currency: {data.reporting_currency || "EUR"}</div>
            </div>
        </Card>
    );
}
